import { ContentLinkType } from "../service/planner/enumerations/IPlannerEnumerations";
/** Returns whether the task has a connected plan exposed by the service-backed field. */ export const hasTaskConnectedPlan = (task)=>!!task?.connectedPlanIds.length;
/** Returns the service-backed connected plan ID. */ export const getConnectedPlanId = (task)=>task.connectedPlanIds[0] ?? null;
/**
 * Returns one PLink per parent task.
 * For example, `/plan/plan-1/task/task-1` and a Teams `subEntityId` with `/v1/plan/plan-1/task/task-1` are one backlink.
 */ export const getPlanParentTaskPLinks = (links)=>{
    const seenRoutes = new Set();
    return links.filter((link)=>{
        if (link.linkType !== ContentLinkType.WebReference) {
            return false;
        }
        const route = getPLinkRoute(link.webUrl);
        if (!route?.taskId) {
            return false;
        }
        const routeKey = JSON.stringify([
            route.planId,
            route.taskId
        ]);
        if (seenRoutes.has(routeKey)) {
            return false;
        }
        seenRoutes.add(routeKey);
        return true;
    });
};
/** Returns whether a ParentTask PLink points to the given task, independent of URL origin. */ export const isParentTaskPLinkForTask = (link, planId, taskId)=>{
    if (link.linkType !== ContentLinkType.WebReference) {
        return false;
    }
    const route = getPLinkRoute(link.webUrl);
    return route?.planId === planId && route.taskId === taskId;
};
/**
 * Parses a PLink deep-link webUrl into its route identifiers.
 * Recognizes direct Planner URLs and Teams entity links whose context contains a Planner subEntityId.
 * Returns `null` when no valid plan segment is present and swallows malformed input as `null`.
 */ export const getPLinkRoute = (webUrl)=>{
    try {
        const url = new URL(webUrl);
        const directRoute = parsePLinkPath(url.pathname);
        if (directRoute) {
            return {
                planId: directRoute.planId,
                taskId: directRoute.taskId,
                planUrl: getDirectPlanUrl(url, directRoute)
            };
        }
        const teamsContext = getTeamsContext(url);
        const teamsRoute = teamsContext ? parsePLinkPath(teamsContext.subEntityId) : null;
        if (!teamsContext || !teamsRoute) {
            return null;
        }
        return {
            planId: teamsRoute.planId,
            taskId: teamsRoute.taskId,
            planUrl: getTeamsPlanUrl(url, teamsContext.context, teamsRoute)
        };
    } catch  {
        return null;
    }
};
/** Returns the plan ID encoded in a PLink web URL. */ export const getConnectedPlanIdFromPLinkUrl = (webUrl)=>getPLinkRoute(webUrl)?.planId ?? null;
/** Returns the master task ID encoded in a plan's parent-task PLink web URL. */ export const getTaskIdFromPlanParentTaskPLink = (link)=>{
    if (link.linkType !== ContentLinkType.WebReference) {
        return null;
    }
    return getPLinkRoute(link.webUrl)?.taskId ?? null;
};
/** Parses plan and task identifiers from a Planner route path. */ const parsePLinkPath = (pathname)=>{
    const pathSegments = pathname.split("/").filter(Boolean);
    const planSegmentIndex = pathSegments.findIndex((segment)=>segment.toLowerCase() === "plan");
    const taskSegmentIndex = pathSegments.findIndex((segment, index)=>index > planSegmentIndex + 1 && segment.toLowerCase() === "task");
    const encodedPlanId = planSegmentIndex >= 0 ? pathSegments[planSegmentIndex + 1] : undefined;
    const encodedTaskId = taskSegmentIndex >= 0 ? pathSegments[taskSegmentIndex + 1] : undefined;
    if (!encodedPlanId) {
        return null;
    }
    return {
        planId: decodeURIComponent(encodedPlanId),
        taskId: encodedTaskId ? decodeURIComponent(encodedTaskId) : null,
        planPath: taskSegmentIndex >= 0 ? `/${pathSegments.slice(0, taskSegmentIndex).join("/")}` : pathname
    };
};
/** Converts a direct Planner task URL into its plan-level URL. */ const getDirectPlanUrl = (url, route)=>{
    if (!route.taskId) {
        return url.toString();
    }
    const planUrl = new URL(url);
    planUrl.pathname = route.planPath;
    planUrl.search = "";
    planUrl.hash = "";
    return planUrl.toString();
};
/** Extracts a Planner sub-entity route from a Teams entity-link context. */ const getTeamsContext = (url)=>{
    if (!url.pathname.toLowerCase().startsWith("/l/entity/")) {
        return null;
    }
    const contextValue = url.searchParams.get("context");
    if (!contextValue) {
        return null;
    }
    const context = JSON.parse(contextValue);
    if (typeof context !== "object" || context === null || !("subEntityId" in context)) {
        return null;
    }
    return typeof context.subEntityId === "string" ? {
        context,
        subEntityId: context.subEntityId
    } : null;
};
/** Converts a Teams task entity link into its plan-level entity link. */ const getTeamsPlanUrl = (url, context, route)=>{
    if (!route.taskId) {
        return url.toString();
    }
    const planUrl = new URL(url);
    planUrl.searchParams.set("context", JSON.stringify({
        ...context,
        subEntityId: route.planPath
    }));
    return planUrl.toString();
};
